import './unlock-user.scss'

import base64 from 'base-64'
import { Formik } from 'formik'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { Container, Form, Image, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { info_x_icon, valid_icon } from 'src/assets'
import i18n from 'src/i18n'
import { NAV_TO_HOMEPAGE, NAV_TO_LOGIN } from 'src/navigation'
import { shoot } from 'src/redux/actions'
import { select_error, select_loading, select_login_token } from 'src/redux/store'
import {
  regex_password_lowercase,
  regex_password_number,
  regex_password_uppercase,
} from 'src/regex'
import { error_t } from 'src/types'
import { ClassNames, format_text } from 'src/utils/--deprecated'
import { Loader, Tooltip } from 'src/widgets'
import Button from 'src/widgets/--deprecated/button/button'
import InputTxt from 'src/widgets/--deprecated/input-txt/input-txt'
import { Recaptcha, submit_captcha } from 'src/widgets/recaptcha/recaptcha'
import * as Yup from 'yup'

/**
 * Unlock User  components
 */
const UnlockUser: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const username = new URLSearchParams(useLocation().search).get('username')
  const magic_link = new URLSearchParams(useLocation().search).get('magicLink')
  const [captcha, set_captcha] = useState('')
  const [password, set_password] = useState('')

  /**
   * Use Selector
   */
  const loading = useSelector(select_loading())
  const error: error_t | undefined = useSelector(select_error())
  const login_token = useSelector(select_login_token)

  /**
   * Use State
   */
  const [modal_info, set_modal_info] = useState({
    title: t('unlock_user.info_modal.title'),
    options: [
      { id: '1', text: t('unlock_user.info_modal.option_1'), valid: '' },
      { id: '2', text: t('unlock_user.info_modal.option_2'), valid: '' },
      { id: '3', text: t('unlock_user.info_modal.option_3'), valid: '' },
      { id: '4', text: t('unlock_user.info_modal.option_4'), valid: '' },
      { id: '5', text: t('unlock_user.info_modal.option_5'), valid: '' },
      { id: '6', text: t('unlock_user.info_modal.option_6'), valid: '' },
      { id: '7', text: t('unlock_user.info_modal.option_7'), valid: '' },
      { id: '8', text: t('unlock_user.info_modal.option_8'), valid: '' },
      { id: '9', text: t('unlock_user.info_modal.option_9'), valid: '' },
    ],
  })

  const [show_modal, set_show_modal] = useState(false)
  const [is_mobile, set_is_mobile] = useState(window.innerWidth < 810)

  /**
   * Functions
   */
  const toggle_modal = (value: boolean) => {
    set_show_modal(value)
  }

  const nav_to_login = () => {
    navigate(NAV_TO_LOGIN)
  }

  const unlock_user_handler = (password: string) => {
    set_password('')
    set_captcha('')
    submit_captcha().then((token: string) => {
      set_password(password)
      set_captcha(token)
    })
  }

  const dismiss_error = () => {
    if (error) {
      dispatch(shoot.reduce_error())
    }
  }

  /**
   * Use effect
   */

  useEffect(() => {
    if (captcha && password) {
      dispatch(
        shoot.saga_unlock_user({
          username,
          password: base64.encode(password),
          magic_link,
          captcha,
        })
      )
    }
  }, [captcha, password])

  useEffect(() => {
    if (login_token) {
      navigate(NAV_TO_HOMEPAGE)
    }
  }, [login_token])

  //Form validation schema
  const validation_schema = Yup.object().shape({
    password: Yup.string()
      .required(() => {
        modal_info.options[0].valid = 'invalid'
        set_modal_info(modal_info)
        return t('new_password.errors.password_required')
      })
      .min(8, () => {
        modal_info.options[0].valid = 'invalid'
        set_modal_info(modal_info)
        return t('new_password.errors.password_invalid')
      })
      .max(7, () => {
        modal_info.options[0].valid = 'valid'
        set_modal_info(modal_info)
      })
      .test('', t('new_password.errors.password_invalid'), (value) => {
        if (value?.match(regex_password_uppercase)) {
          modal_info.options[1].valid = 'valid'
          set_modal_info(modal_info)
          return true
        } else {
          modal_info.options[1].valid = 'invalid'
          set_modal_info(modal_info)
          return false
        }
      })
      .test('', t('new_password.errors.password_invalid'), (value) => {
        if (value?.match(regex_password_lowercase)) {
          modal_info.options[2].valid = 'valid'
          set_modal_info(modal_info)
          return true
        } else {
          modal_info.options[2].valid = 'invalid'
          set_modal_info(modal_info)
          return false
        }
      })
      .test('', t('new_password.errors.password_invalid'), (value) => {
        if (value?.match(regex_password_number)) {
          modal_info.options[3].valid = 'valid'
          set_modal_info(modal_info)
          return true
        } else {
          modal_info.options[3].valid = 'invalid'
          set_modal_info(modal_info)
          return false
        }
      }),
    passwordConfirmed: Yup.string()
      .required(t('unlock_user.errors.password_required'))
      .test('passwords-match', t('unlock_user.errors.password_different'), function (value) {
        return this.parent.password === value
      }),
  })

  return (
    <Container fluid>
      {loading ? (
        <div className="login-loader">
          <Loader />{' '}
        </div>
      ) : (
        <div>
          <Row>
            <div className="unlock-user-button-back">
              <Button name="text_059881" onClick={nav_to_login} padding="0">
                <div className="left-arrow-green" />
                <div> {t('unlock_user.back_button')}</div>
              </Button>
            </div>
          </Row>

          <Row className="unlock-user-container">
            <div onClick={() => toggle_modal(false)}>
              <div
                className={ClassNames(
                  'unlock-user-title',
                  is_mobile ? 'unnamed-character-style-30' : 'unnamed-character-style-29'
                )}
              >
                {t('unlock_user.title')}
              </div>
              <div
                className={ClassNames(
                  'unlock-user-sutitle',
                  is_mobile ? 'unnamed-character-style-15-26-7f7f7f' : 'unnamed-character-style-18'
                )}
              >
                {format_text(t('unlock_user.subtitle'), '')}
              </div>
              <Recaptcha />
              <Formik
                key="password"
                validationSchema={validation_schema}
                initialValues={{
                  password: '',
                  passwordConfirmed: '',
                }}
                onSubmit={(values) => {
                  unlock_user_handler(values.password)
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="password" className="unlock-user-input-container">
                      <div className="unlock-user-input-icon-container">
                        <Form.Label
                          className={ClassNames(
                            'unlock-user-input-label',
                            is_mobile
                              ? 'unnamed-character-style-27-13-22'
                              : 'unnamed-character-style-48'
                          )}
                        >
                          {t('unlock_user.forms.new_password')}
                        </Form.Label>
                        <Tooltip placement="top">
                          <div>
                            <div className="text-left mb-2">{modal_info.title}</div>
                            {modal_info.options.map((data, index) => (
                              <li
                                key={index}
                                className={`text-left ${
                                  data.valid === 'valid'
                                    ? 'c-159881'
                                    : data.valid === 'invalid'
                                    ? 'c-862633'
                                    : ''
                                }`}
                              >
                                {data.text}
                                {data.valid === 'valid' ? (
                                  <Image src={valid_icon} width={12} className="ml-2" />
                                ) : data.valid === 'invalid' ? (
                                  <Image src={info_x_icon} className="ml-2" />
                                ) : (
                                  ''
                                )}
                              </li>
                            ))}
                          </div>
                        </Tooltip>{' '}
                      </div>
                      <InputTxt
                        id="password"
                        placeholder={t('unlock_user.forms.new_password_placeholder')}
                        type="password"
                        value={values.password}
                        on_change={(e: ChangeEvent<HTMLInputElement>) => {
                          handleChange(e)
                          handleBlur(e)
                          dismiss_error()
                          setFieldValue('password', e.target.value)
                        }}
                        validators={[
                          {
                            is_valid: () =>
                              !((touched.password && errors.password) || values.password === ''),
                            text: errors.password || '',
                          },
                        ]}
                      />
                      {error && !error.is_tech && !(touched.password && errors.password) ? (
                        <div className="error-character">{error?.description_fe}</div>
                      ) : null}
                    </Form.Group>

                    <Form.Group controlId="passwordConfirmed">
                      <Form.Label
                        className={ClassNames(
                          'unlock-user-input-label',
                          'unlock-user-input-label--confirm-password',
                          is_mobile
                            ? 'unnamed-character-style-27-13-22'
                            : 'unnamed-character-style-48'
                        )}
                      >
                        {t('unlock_user.forms.confirm_password')}
                      </Form.Label>

                      <InputTxt
                        id="passwordConfirmed"
                        placeholder={t('unlock_user.forms.confirm_password_placeholder')}
                        type="password"
                        value={values.passwordConfirmed}
                        on_change={(e: ChangeEvent<HTMLInputElement>) => {
                          handleChange(e)
                          handleBlur(e)
                          dismiss_error()
                          setFieldValue('passwordConfirmed', e.target.value)
                        }}
                        validators={[
                          {
                            is_valid: () =>
                              !(
                                (touched.passwordConfirmed && errors.passwordConfirmed) ||
                                values.passwordConfirmed === ''
                              ),
                            text: errors.passwordConfirmed || '',
                          },
                        ]}
                      />
                    </Form.Group>

                    <div className="unlock-user-button-submit">
                      <Button
                        justify_content="center"
                        name={'full_059881'}
                        disabled={
                          values.password == '' ||
                          values.passwordConfirmed == '' ||
                          errors.password != undefined ||
                          errors.passwordConfirmed != undefined
                        }
                      >
                        <div>{t('unlock_user.forms.btn_label')}</div>
                        <div className="right-arrow-white" />
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Row>
        </div>
      )}
    </Container>
  )
}

export default UnlockUser
